import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { DEMO_TEMPLATES, DEMO_AUTOMATION_CONFIG, DEMO_BUSINESS, DEMO_CUSTOM_TEMPLATES } from '../demoData';
import { isChannelVisible, filterChannelAccess } from '../config/featureFlags';
import {
  fillPreviewVars,
  displayTemplateBody,
  getEmailCtaLabel,
  REVIEW_LINK_PLACEHOLDER,
  BOOKING_PLACEHOLDER,
  SMS_MAX_LENGTH,
} from '../utils/templatePreview';
import { useScrollLock } from '../utils/useScrollLock';

const CHANNEL_LABELS = {
  email: '📧 Email',
  sms: '💬 SMS',
  whatsapp: '📱 WhatsApp',
};

const WA_TEMPLATE_OPTIONS = {
  reminder: [
    { name: 'loop_visitor_1', body: "Hi {{customer_name}}, you deserve another amazing experience! Let's make it happen. {{booking_link}}", button: 'Book Now' },
    { name: 'loop_visitor_2', body: "Hi {{customer_name}}, this is your sign to book another visit. You deserve it! {{booking_link}}", button: 'Book Now' },
    { name: 'loop_visitor_3', body: "Hey {{customer_name}}, your spot is waiting for you! Don't keep us waiting! {{booking_link}}", button: 'Book Now' },
    { name: 'loop_visitor_4', body: "Hi {{customer_name}}, just a gentle nudge. We'd love to see you again! {{booking_link}}", button: 'Book Now' },
  ],
  review_request: [
    { name: 'loop_visitor_review_1', body: "Hey {{customer_name}}, it was a pleasure taking care of you. We'd be so grateful if you could tell others about your experience. {{review_link}}", button: 'Leave a review' },
    { name: 'loop_visitor_review_2', body: "Hi {{customer_name}}, we truly enjoyed having you. We'd love it if you shared your experience - it really helps us out. {{review_link}}", button: 'Leave a review' },
    { name: 'loop_visitor_review_3', body: "Hello {{customer_name}}, your opinion means the world to us. If you have 30 seconds, a quick review would make our week. {{review_link}}", button: 'Leave a review' },
  ],
};

const AI_SUGGESTIONS = {
  review_request: {
    email: {
      subject: "We'd love your feedback, {{customer_name}}",
      body: "Hi {{customer_name}},\n\nWe just wanted to reach out and say thank you for choosing us. It truly means the world to us that you trusted us with your time.\n\nIf you have a spare moment, we'd be incredibly grateful if you could share your experience with others.\n\n{{review_link}}\n\nThank you so much!",
    },
    sms: "Hi {{customer_name}}, we truly enjoyed having you. We'd love it if you shared your experience — it really helps us out: {{review_link}}",
    whatsapp: "Hi {{customer_name}}, we truly enjoyed having you. We'd love it if you shared your experience — it really helps us out: {{review_link}}",
  },
  reminder: {
    email: {
      subject: "We've been thinking about you, {{customer_name}}",
      body: "Hi {{customer_name}},\n\nIt's been a little while, and we've been thinking about you.\n\nWhenever you're ready to come back, we'd love to take care of you again.\n\n{{booking_link}}\n\nWe're here whenever you're ready — no rush, no pressure.",
    },
    sms: "Hi {{customer_name}}, we've been thinking about you! We'd love to take care of you again — come back and see us. {{booking_link}}",
    whatsapp: "Hi {{customer_name}}, we've been thinking about you! We'd love to take care of you again — come back and see us. {{booking_link}}",
  },
};

const configBadgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 12,
  background: 'var(--bg)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: '8px 14px',
  fontSize: 12,
  color: 'var(--text-secondary)',
  flexWrap: 'wrap',
};

const separatorStyle = {
  borderLeft: '1px solid var(--border)',
  paddingLeft: 12,
  display: 'flex',
  alignItems: 'center',
  gap: 4,
};

const numberInputStyle = {
  width: 52,
  padding: '2px 6px',
  fontSize: 12,
  border: '1px solid var(--border)',
  borderRadius: 4,
  textAlign: 'center',
  background: 'white',
};

const SEND_TIME_OPTIONS = [
  { value: 6, label: '6:00 AM' },
  { value: 7, label: '7:00 AM' },
  { value: 8, label: '8:00 AM' },
  { value: 9, label: '9:00 AM' },
  { value: 10, label: '10:00 AM' },
  { value: 11, label: '11:00 AM' },
  { value: 12, label: '12:00 PM' },
  { value: 13, label: '1:00 PM' },
  { value: 14, label: '2:00 PM' },
  { value: 15, label: '3:00 PM' },
  { value: 16, label: '4:00 PM' },
  { value: 17, label: '5:00 PM' },
  { value: 18, label: '6:00 PM' },
  { value: 19, label: '7:00 PM' },
  { value: 20, label: '8:00 PM' },
  { value: 21, label: '9:00 PM' },
];

function formatHour(h) {
  const opt = SEND_TIME_OPTIONS.find((o) => o.value === h);
  return opt ? opt.label : `${h}:00`;
}

function SmsCharCounter({ text, channel }) {
  if (channel !== 'sms') return null;
  const length = text?.length || 0;
  const overLimit = length > SMS_MAX_LENGTH;
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: 6,
      fontSize: 12,
      color: overLimit ? '#DC2626' : 'var(--text-muted)',
    }}>
      <span>{overLimit ? `SMS must be ${SMS_MAX_LENGTH} characters or fewer` : `${SMS_MAX_LENGTH - length} characters remaining`}</span>
      <span>{length}/{SMS_MAX_LENGTH}</span>
    </div>
  );
}

function EmailPreviewAction({ url, label }) {
  return (
    <span style={{ display: 'block', margin: '20px 0 18px' }}>
      <a href={url} target="_blank" rel="noopener noreferrer" aria-label={label} style={{
        display: 'inline-block', padding: '12px 22px',
        background: '#1f4f8f', color: '#ffffff', border: '1px solid #1f4f8f',
        borderRadius: 6, fontWeight: 700, fontSize: 15, lineHeight: '20px', textDecoration: 'none',
      }}>
        {label}
      </a>
      <span style={{ display: 'block', marginTop: 14, fontSize: 12, lineHeight: 1.5, color: '#6b7280' }}>
        If the button doesn&apos;t work, copy and paste this link into your browser:<br />
        <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#4b5563', textDecoration: 'underline', wordBreak: 'break-all' }}>
          {url}
        </a>
      </span>
    </span>
  );
}

function RichLine({ text, channel, themeColor, reviewCtaLabel }) {
  if (text.includes(REVIEW_LINK_PLACEHOLDER)) {
    const [before, url] = text.split(REVIEW_LINK_PLACEHOLDER);
    return (
      <span>
        {before}
        {channel === 'email' ? (
          <EmailPreviewAction url={url} label={reviewCtaLabel || 'Leave a Review'} />
        ) : (
          <a href={url} target="_blank" rel="noopener noreferrer" style={{
            color: channel === 'whatsapp' ? '#075e54' : (themeColor || '#6C5CE7'),
            textDecoration: 'underline', fontWeight: 600,
          }}>
            Leave a review
          </a>
        )}
      </span>
    );
  }

  if (text.includes(BOOKING_PLACEHOLDER)) {
    const [before, url] = text.split(BOOKING_PLACEHOLDER);
    return (
      <span>
        {before}
        {channel === 'email' ? (
          <EmailPreviewAction url={url} label="Book a Visit" />
        ) : (
          <a href={url} target="_blank" rel="noopener noreferrer" style={{
            color: channel === 'whatsapp' ? '#075e54' : (themeColor || '#6C5CE7'),
            textDecoration: 'underline', fontWeight: 600,
          }}>
            Book now
          </a>
        )}
      </span>
    );
  }

  return <span>{text}</span>;
}

function getWaButtonLabel(tpl) {
  const options = WA_TEMPLATE_OPTIONS[tpl.category];
  if (!options) return null;
  const match = options.find((o) => o.name === tpl.waTemplateName);
  if (match) return match.button;
  return tpl.category === 'reminder' ? 'Book Now' : 'Leave a review';
}

function PreviewModal({ tpl, business, onClose }) {
  useScrollLock(!!tpl);
  const [reviewPreviewOccurrence, setReviewPreviewOccurrence] = useState(1);
  useEffect(() => setReviewPreviewOccurrence(1), [tpl?.id]);
  if (!tpl) return null;

  const filledSubject = fillPreviewVars(tpl.subject, business, { rawLinks: true, channel: tpl.channel || 'email' });
  const filledBody = fillPreviewVars(tpl.body, business, { channel: tpl.channel || 'email' });
  const bizName = business?.name || 'Your Business';
  const previewOccurrence = tpl.previewOccurrence || reviewPreviewOccurrence;
  const reviewCtaLabel = getEmailCtaLabel('review_request', previewOccurrence);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: tpl.channel === 'email' ? 620 : 420 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0 }}>
            {tpl.channel === 'email' ? '📧 Email Preview' : tpl.channel === 'sms' ? '💬 SMS Preview' : '📱 WhatsApp Preview'}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-secondary)' }}>
            &times;
          </button>
        </div>

        {tpl.channel === 'email' && tpl.category === 'review_request' && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }} aria-label="Review email preview type">
            <button
              type="button"
              className={previewOccurrence === 1 ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
              aria-pressed={previewOccurrence === 1}
              onClick={() => setReviewPreviewOccurrence(1)}
            >
              Initial request
            </button>
            <button
              type="button"
              className={previewOccurrence > 1 ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
              aria-pressed={previewOccurrence > 1}
              onClick={() => setReviewPreviewOccurrence(2)}
            >
              Follow-up reminder
            </button>
          </div>
        )}

        {tpl.channel === 'email' ? (
          <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)', background: '#ffffff' }}>
            <div style={{ padding: '16px 28px', borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ color: '#6b7280', fontSize: 11, marginBottom: 4 }}>SUBJECT</div>
              <div style={{ color: '#242424', fontSize: 16, fontWeight: 600 }}>{filledSubject || '(No subject)'}</div>
            </div>
            <div style={{ background: 'white', padding: '28px 32px' }}>
              {filledBody.split('\n\n').map((para, i) => (
                <p key={i} style={{ margin: '0 0 14px', fontSize: 14, lineHeight: 1.7, color: '#374151' }}>
                  {para.split('\n').map((line, j) => (
                    <React.Fragment key={j}>
                      <RichLine text={line} channel="email" themeColor={business?.mailThemeColor} reviewCtaLabel={reviewCtaLabel} />
                      {j < para.split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </p>
              ))}
            </div>
            <div style={{ padding: '16px 32px', borderTop: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: 12, lineHeight: 1.5, color: '#6b7280' }}>Sent on behalf of {bizName}, following a customer visit.</div>
              <div style={{ marginTop: 8, fontSize: 12, color: '#4b5563', textDecoration: 'underline' }}>Unsubscribe from these emails</div>
            </div>
          </div>
        ) : (
          <div style={{
            background: tpl.channel === 'whatsapp' ? '#e5ddd5' : '#f3f4f6',
            borderRadius: 12,
            padding: 20,
            minHeight: 120,
            ...(tpl.channel === 'whatsapp' ? { backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M0 0h60v60H0z\' fill=\'%23d4cfc4\' opacity=\'.15\'/%3E%3C/svg%3E")' } : {}),
          }}>
            <div style={{
              background: tpl.channel === 'whatsapp' ? '#dcf8c6' : 'white',
              borderRadius: tpl.channel === 'whatsapp' ? '0 12px 12px 12px' : 12,
              padding: '12px 16px',
              maxWidth: '85%',
              boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
              fontSize: 14,
              lineHeight: 1.6,
              color: '#1a1a1a',
            }}>
              {filledBody.split('\n').map((line, i) => (
                <React.Fragment key={i}>
                  <RichLine text={line} channel={tpl.channel} themeColor={business?.mailThemeColor} />
                  {i < filledBody.split('\n').length - 1 && <br />}
                </React.Fragment>
              ))}
              {tpl.channel === 'whatsapp' && (
                <div style={{
                  borderTop: '1px solid rgba(0,0,0,0.1)',
                  marginTop: 8,
                  paddingTop: 8,
                  textAlign: 'center',
                }}>
                  <span style={{ color: '#075e54', fontWeight: 600, fontSize: 14 }}>
                    📎 {getWaButtonLabel(tpl)}
                  </span>
                </div>
              )}
            </div>
            <div style={{ fontSize: 11, color: '#8696a0', marginTop: 6, textAlign: 'right', maxWidth: '85%' }}>
              {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        )}

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function TimingBadge({ config, editingConfig, configForm, setConfigForm, onEdit, onSave, onCancel, savingConfig, section }) {
  const editButtons = editingConfig ? (
    <div style={{ display: 'flex', gap: 6 }}>
      <button className="btn btn-primary btn-sm" onClick={onSave} disabled={savingConfig}
        style={{ fontSize: 11, padding: '4px 10px' }}>
        {savingConfig ? <span className="spinner" style={{ width: 12, height: 12 }} /> : 'Save'}
      </button>
      <button className="btn btn-secondary btn-sm" onClick={onCancel}
        style={{ fontSize: 11, padding: '4px 10px' }}>Cancel</button>
    </div>
  ) : (
    <button className="btn btn-secondary btn-sm" onClick={onEdit}
      style={{ fontSize: 11, padding: '4px 10px' }}>Edit</button>
  );

  if (section === 'review_request') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div style={configBadgeStyle}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            ⚡ <strong>Timing:</strong>{' '}
            {editingConfig ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <input
                  type="number"
                  min="0"
                  value={configForm.reviewDelayDays}
                  onChange={(e) => setConfigForm({ ...configForm, reviewDelayDays: parseInt(e.target.value) || 0 })}
                  style={numberInputStyle}
                /> days after visit
              </span>
            ) : (
              config.reviewDelayDays === 0 ? 'Immediately after visit' : `${config.reviewDelayDays} days after visit`
            )}
          </span>
          <span style={separatorStyle}>
            🔁 <strong>Repeat:</strong>{' '}
            {editingConfig ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                every <input
                  type="number"
                  min="0"
                  value={configForm.reviewFreqDays}
                  onChange={(e) => setConfigForm({ ...configForm, reviewFreqDays: parseInt(e.target.value) || 0 })}
                  style={numberInputStyle}
                /> days
                <span style={{ fontSize: 11, opacity: 0.7 }}>(0 = one-time)</span>
              </span>
            ) : (
              config.reviewFreqDays > 0 ? `Every ${config.reviewFreqDays} days` : 'One-time'
            )}
          </span>
          <span style={separatorStyle}>
            📊 <strong>Max:</strong>{' '}
            {editingConfig ? (
              <input
                type="number"
                min="1"
                max="10"
                value={configForm.maxReviews}
                onChange={(e) => setConfigForm({ ...configForm, maxReviews: Math.min(10, Math.max(1, parseInt(e.target.value) || 1)) })}
                style={{ ...numberInputStyle, width: 44 }}
              />
            ) : (
              `${config.maxReviews} per customer`
            )}
          </span>
        </div>
        <div style={{ ...configBadgeStyle, marginTop: 6 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            🕐 <strong>Send at:</strong>{' '}
            {editingConfig ? (
              <select
                value={configForm.reviewSendHour ?? 10}
                onChange={(e) => setConfigForm({ ...configForm, reviewSendHour: parseInt(e.target.value) })}
                style={{ fontSize: 12, padding: '2px 6px', border: '1px solid var(--border)', borderRadius: 4, background: 'white' }}
              >
                {SEND_TIME_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : (
              formatHour(config.reviewSendHour ?? 10)
            )}
          </span>
          <span style={separatorStyle}>
            📅 <strong>Prefer weekends:</strong>{' '}
            {editingConfig ? (
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={configForm.reviewPreferWeekends ?? false}
                  onChange={(e) => setConfigForm({ ...configForm, reviewPreferWeekends: e.target.checked })}
                  style={{ width: 14, height: 14 }}
                />
                {configForm.reviewPreferWeekends ? 'Yes' : 'No'}
              </label>
            ) : (
              config.reviewPreferWeekends ? 'Yes (Sat/Sun)' : 'No'
            )}
          </span>
        </div>
        {editButtons}
        <div style={{ width: '100%', fontSize: 11, color: 'var(--text-secondary)', opacity: 0.8, marginTop: 2 }}>
          Customers who have already clicked a review link will not receive further review requests.
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <div style={configBadgeStyle}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          🕐 <strong>First send:</strong>{' '}
          {editingConfig ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <input
                type="number"
                min="1"
                value={configForm.reminderDelayDays}
                onChange={(e) => setConfigForm({ ...configForm, reminderDelayDays: parseInt(e.target.value) || 1 })}
                style={numberInputStyle}
              /> days after visit
            </span>
          ) : (
            `${config.reminderDelayDays} days after visit`
          )}
        </span>
        <span style={separatorStyle}>
          🔁 <strong>Repeat:</strong>{' '}
          {editingConfig ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              every <input
                type="number"
                min="0"
                value={configForm.reminderFreqDays}
                onChange={(e) => setConfigForm({ ...configForm, reminderFreqDays: parseInt(e.target.value) || 0 })}
                style={numberInputStyle}
              /> days
              <span style={{ fontSize: 11, opacity: 0.7 }}>(0 = one-time)</span>
            </span>
          ) : (
            config.reminderFreqDays > 0 ? `Every ${config.reminderFreqDays} days` : 'One-time'
          )}
        </span>
        <span style={separatorStyle}>
          📊 <strong>Max:</strong>{' '}
          {editingConfig ? (
            <input
              type="number"
              min="1"
              max="100"
              value={configForm.maxReminders}
              onChange={(e) => setConfigForm({ ...configForm, maxReminders: Math.min(100, Math.max(1, parseInt(e.target.value) || 1)) })}
              style={{ ...numberInputStyle, width: 52 }}
            />
          ) : (
            `${config.maxReminders} per customer`
          )}
        </span>
      </div>
      <div style={{ ...configBadgeStyle, marginTop: 6 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          🕐 <strong>Send at:</strong>{' '}
          {editingConfig ? (
            <select
              value={configForm.reminderSendHour ?? 10}
              onChange={(e) => setConfigForm({ ...configForm, reminderSendHour: parseInt(e.target.value) })}
              style={{ fontSize: 12, padding: '2px 6px', border: '1px solid var(--border)', borderRadius: 4, background: 'white' }}
            >
              {SEND_TIME_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          ) : (
            formatHour(config.reminderSendHour ?? 10)
          )}
        </span>
        <span style={separatorStyle}>
          📅 <strong>Prefer weekends:</strong>{' '}
          {editingConfig ? (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={configForm.reminderPreferWeekends ?? false}
                onChange={(e) => setConfigForm({ ...configForm, reminderPreferWeekends: e.target.checked })}
                style={{ width: 14, height: 14 }}
              />
              {configForm.reminderPreferWeekends ? 'Yes' : 'No'}
            </label>
          ) : (
            config.reminderPreferWeekends ? 'Yes (Sat/Sun)' : 'No'
          )}
        </span>
      </div>
      {editButtons}
    </div>
  );
}

export default function Templates() {
  const { user, isDemo } = useAuth();
  const toast = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ subject: '', body: '', waTemplateName: '' });
  const [saving, setSaving] = useState(false);
  const [improvingId, setImprovingId] = useState(null);
  const [previewTpl, setPreviewTpl] = useState(null);
  const [business, setBusiness] = useState(null);
  const [channelAccess, setChannelAccess] = useState(user?.channelAccess || { email: true, sms: false, whatsapp: false });

  const [showCustomSend, setShowCustomSend] = useState(false);
  const [customForm, setCustomForm] = useState({ channel: 'email', subject: '', body: '', category: 'marketing' });
  const [showCustomerSelect, setShowCustomerSelect] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [sendingCustom, setSendingCustom] = useState(false);
  const [savedCustomTemplates, setSavedCustomTemplates] = useState([]);
  const [savingCustomTemplate, setSavingCustomTemplate] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [sendFromSaved, setSendFromSaved] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });
  const [editingSavedId, setEditingSavedId] = useState(null);
  const [activeSegment, setActiveSegment] = useState(null);

  const [autoConfig, setAutoConfig] = useState(null);
  const [editingConfig, setEditingConfig] = useState(null);
  const [configForm, setConfigForm] = useState({});
  const [savingConfig, setSavingConfig] = useState(false);
  const [togglingRefresh, setTogglingRefresh] = useState(false);

  useEffect(() => {
    if (isDemo) {
      const demoAccess = filterChannelAccess({ email: true, sms: true, whatsapp: true });
      setTemplates(DEMO_TEMPLATES.filter(t => isChannelVisible(t.channel) && demoAccess[t.channel] !== false));
      setAutoConfig(DEMO_AUTOMATION_CONFIG);
      setBusiness(DEMO_BUSINESS);
      setChannelAccess(demoAccess);
      setLoading(false);
      return;
    }
    api.getTemplatePageData()
      .then((d) => {
        const access = d.channelAccess ? filterChannelAccess(d.channelAccess) : { email: true, sms: false, whatsapp: false };
        setTemplates((d.templates || []).filter(t => isChannelVisible(t.channel) && access[t.channel] !== false));
        setAutoConfig(d.config);
        setBusiness(d.business);
        setSavedCustomTemplates(d.savedCustomTemplates || []);
        if (d.channelAccess) setChannelAccess(access);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [isDemo]);

  const startEdit = (tpl) => {
    setEditing(tpl.id);
    setEditForm({ subject: tpl.subject || '', body: tpl.body || '', waTemplateName: tpl.waTemplateName || '' });
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditForm({ subject: '', body: '', waTemplateName: '' });
  };

  const handleCycleWaTemplate = (tpl) => {
    const options = WA_TEMPLATE_OPTIONS[tpl.category];
    if (!options) return;
    const currentBody = editing === tpl.id ? editForm.body : tpl.body;
    const currentIdx = options.findIndex((o) => o.body === currentBody);
    const nextIdx = (currentIdx + 1) % options.length;
    const next = options[nextIdx];
    if (editing !== tpl.id) {
      setEditing(tpl.id);
    }
    setEditForm({ subject: tpl.subject || '', body: next.body, waTemplateName: next.name });
  };

  const saveTemplate = async () => {
    const tpl = templates.find((t) => t.id === editing);
    if (tpl?.channel === 'sms' && (editForm.body || '').length > SMS_MAX_LENGTH) {
      toast.error(`SMS must be ${SMS_MAX_LENGTH} characters or fewer`);
      return;
    }

    setSaving(true);
    const payload = { subject: editForm.subject, body: editForm.body };
    if (tpl?.channel === 'whatsapp' && editForm.waTemplateName) {
      payload.waTemplateName = editForm.waTemplateName;
    }
    if (isDemo) {
      setTemplates((prev) =>
        prev.map((t) => (t.id === editing ? { ...t, ...payload } : t))
      );
      setEditing(null);
      toast.success('Template saved');
      setSaving(false);
      return;
    }
    try {
      const result = await api.updateTemplate(editing, payload);
      setTemplates((prev) =>
        prev.map((t) => (t.id === editing ? result.template : t))
      );
      setEditing(null);
      toast.success('Template saved');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleImprove = async (tpl) => {
    setImprovingId(tpl.id);
    const thinkDelay = () => new Promise((r) => setTimeout(r, 1200 + Math.random() * 800));
    if (isDemo) {
      await thinkDelay();
      const catSuggestions = AI_SUGGESTIONS[tpl.category] || AI_SUGGESTIONS.review_request;
      const improved = catSuggestions[tpl.channel] || catSuggestions.email;
      if (editing !== tpl.id) startEdit(tpl);
      if (tpl.channel === 'email' && improved?.body) {
        setEditForm((prev) => ({ ...prev, body: improved.body, subject: improved.subject ?? prev.subject }));
      } else {
        setEditForm((prev) => ({ ...prev, body: improved }));
      }
      toast.success('AI-improved version applied!');
      setImprovingId(null);
      return;
    }
    try {
      const [result] = await Promise.all([
        api.improveMessage(editForm.body || tpl.body, tpl.category, tpl.channel),
        thinkDelay(),
      ]);
      const improved = result.suggestions?.[0];
      if (!improved) {
        toast.error('No suggestion available');
        return;
      }
      const improvedBody = improved?.body ?? improved;
      const improvedSubject = improved?.subject ?? null;
      if (editing !== tpl.id) {
        setEditing(tpl.id);
        setEditForm({
          subject: improvedSubject ?? tpl.subject ?? '',
          body: improvedBody,
          waTemplateName: tpl.waTemplateName || '',
        });
      } else {
        setEditForm((prev) => ({
          ...prev,
          body: improvedBody,
          ...(improvedSubject ? { subject: improvedSubject } : {}),
        }));
      }
      toast.success('AI-improved version applied!');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setImprovingId(null);
    }
  };

  const startConfigEdit = (sectionKey) => {
    setEditingConfig(sectionKey);
    setConfigForm({ ...autoConfig });
  };

  const cancelConfigEdit = () => {
    setEditingConfig(null);
    setConfigForm({});
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    if (isDemo) {
      setAutoConfig({ ...configForm });
      setEditingConfig(null);
      toast.success('Automation schedule saved');
      setSavingConfig(false);
      return;
    }
    try {
      const result = await api.updateAutomationConfig(configForm);
      setAutoConfig(result.config);
      setEditingConfig(null);
      const { rescheduled } = result;
      if (rescheduled && (rescheduled.updated > 0 || rescheduled.cancelled > 0)) {
        const parts = [];
        if (rescheduled.updated > 0) parts.push(`${rescheduled.updated} rescheduled`);
        if (rescheduled.cancelled > 0) parts.push(`${rescheduled.cancelled} cancelled`);
        toast.success(`Automation schedule saved — ${parts.join(', ')} queued message${(rescheduled.updated + rescheduled.cancelled) !== 1 ? 's' : ''}`);
      } else {
        toast.success('Automation schedule saved');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingConfig(false);
    }
  };

  const handleToggleAutoRefresh = async () => {
    if (togglingRefresh) return;
    const newValue = !autoConfig.autoRefreshTemplates;
    setTogglingRefresh(true);
    if (isDemo) {
      setAutoConfig(prev => ({ ...prev, autoRefreshTemplates: newValue }));
      toast.success(newValue ? 'Monthly template refresh enabled' : 'Monthly template refresh disabled');
      setTogglingRefresh(false);
      return;
    }
    try {
      const result = await api.updateAutomationConfig({ ...autoConfig, autoRefreshTemplates: newValue });
      setAutoConfig(result.config);
      toast.success(newValue ? 'Monthly template refresh enabled' : 'Monthly template refresh disabled');
    } catch (err) {
      toast.error(err.message || 'Failed to update setting');
    } finally {
      setTogglingRefresh(false);
    }
  };

  const loadCustomers = async () => {
    setLoadingCustomers(true);
    try {
      if (isDemo) {
        setCustomers([
          { id: 1, name: 'Sarah Johnson', email: 'sarah@example.com', phone: '+1234567890' },
          { id: 2, name: 'Mike Chen', email: 'mike@example.com', phone: '+1987654321' },
          { id: 3, name: 'Emily Davis', email: 'emily@example.com', phone: null },
          { id: 4, name: 'Alex Turner', email: 'alex@example.com', phone: '+1122334455' },
          { id: 5, name: 'Lisa Park', email: 'lisa@example.com', phone: '+1555666777' },
        ]);
      } else {
        const result = await api.getCustomers({ limit: 200 });
        setCustomers(result.customers || []);
      }
    } catch (err) {
      toast.error('Failed to load customers');
    } finally {
      setLoadingCustomers(false);
    }
  };

  const loadSavedTemplates = async () => {
    if (isDemo) {
      setSavedCustomTemplates(DEMO_CUSTOM_TEMPLATES);
      return;
    }
    try {
      const result = await api.getSavedTemplates();
      setSavedCustomTemplates(result.templates || []);
    } catch (err) {
      console.error('Failed to load saved templates:', err);
    }
  };

  const handleSaveCustomTemplate = async () => {
    if (!customForm.body.trim()) {
      toast.error('Message body is required to save');
      return;
    }
    if (customForm.channel === 'email' && !customForm.subject.trim()) {
      toast.error('Email subject is required to save');
      return;
    }
    if (customForm.channel === 'sms' && customForm.body.length > SMS_MAX_LENGTH) {
      toast.error(`SMS must be ${SMS_MAX_LENGTH} characters or fewer`);
      return;
    }

    setSavingCustomTemplate(true);
    try {
      if (isDemo) {
        if (editingSavedId) {
          setSavedCustomTemplates(prev => prev.map(t => t.id === editingSavedId ? { ...t, ...customForm } : t));
        } else {
          const newTpl = { id: Date.now(), ...customForm, createdAt: new Date().toISOString() };
          setSavedCustomTemplates(prev => [newTpl, ...prev]);
        }
        toast.success(editingSavedId ? 'Template updated!' : 'Template saved!');
      } else {
        if (editingSavedId) {
          await api.deleteCustomTemplate(editingSavedId);
        }
        const result = await api.saveCustomTemplate({
          channel: customForm.channel,
          subject: customForm.subject,
          body: customForm.body,
          category: customForm.category,
        });
        if (editingSavedId) {
          setSavedCustomTemplates(prev => prev.map(t => t.id === editingSavedId ? result.template : t));
        } else {
          setSavedCustomTemplates(prev => [result.template, ...prev]);
        }
        toast.success(editingSavedId ? 'Template updated!' : 'Template saved!');
      }
      setCustomForm({ channel: 'email', subject: '', body: '', category: 'marketing' });
      setEditingSavedId(null);
      setShowCustomSend(false);
    } catch (err) {
      toast.error(err.message || 'Failed to save template');
    } finally {
      setSavingCustomTemplate(false);
    }
  };

  const handleDeleteSavedTemplate = async (id) => {
    if (isDemo) {
      setSavedCustomTemplates(prev => prev.filter(t => t.id !== id));
      setDeleteConfirm({ open: false, id: null });
      return;
    }
    try {
      await api.deleteCustomTemplate(id);
      setSavedCustomTemplates(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      toast.error(err.message || 'Failed to delete');
    }
    setDeleteConfirm({ open: false, id: null });
  };

  const handleEditSaved = (tpl) => {
    setCustomForm({
      channel: tpl.channel,
      subject: tpl.subject || '',
      body: tpl.body,
      category: tpl.category,
    });
    setEditingSavedId(tpl.id);
  };

  const closeCustomForm = () => {
    setShowCustomSend(false);
    setEditingSavedId(null);
    setCustomForm({ channel: 'email', subject: '', body: '', category: 'marketing' });
  };

  const handleUseSavedTemplate = (tpl) => {
    setSendFromSaved(tpl);
    setCustomerSearch('');
    setSelectedCustomers([]);
    loadCustomers();
    setShowCustomerSelect(true);
  };

  const handleCustomSend = async () => {
    if (selectedCustomers.length === 0) {
      toast.error('Select at least one customer');
      return;
    }

    const sendForm = sendFromSaved || customForm;
    if (!sendForm.body.trim()) {
      toast.error('Message body is required');
      return;
    }
    if (sendForm.channel === 'email' && !sendForm.subject?.trim()) {
      toast.error('Email subject is required');
      return;
    }
    if (sendForm.channel === 'sms' && sendForm.body.length > SMS_MAX_LENGTH) {
      toast.error(`SMS must be ${SMS_MAX_LENGTH} characters or fewer`);
      return;
    }

    setSendingCustom(true);
    try {
      if (isDemo) {
        await new Promise(r => setTimeout(r, 1500));
        toast.success(`Messages queued for ${selectedCustomers.length} customer(s)`);
      } else {
        await api.sendCustomTemplate({
          channel: sendForm.channel,
          subject: sendForm.subject || '',
          body: sendForm.body,
          category: sendForm.category,
          customerIds: selectedCustomers.map(c => c.id),
        });
        toast.success(`Messages queued for ${selectedCustomers.length} customer(s)`);
      }

      if (!sendFromSaved && sendForm.body.trim()) {
        const alreadySaved = savedCustomTemplates.some(t =>
          t.body === sendForm.body && t.channel === sendForm.channel && t.subject === (sendForm.subject || null)
        );
        if (!alreadySaved) {
          try {
            if (isDemo) {
              const newTpl = { id: Date.now(), ...sendForm, createdAt: new Date().toISOString() };
              setSavedCustomTemplates(prev => [newTpl, ...prev]);
            } else {
              const result = await api.saveCustomTemplate({
                channel: sendForm.channel,
                subject: sendForm.subject || '',
                body: sendForm.body,
                category: sendForm.category,
              });
              setSavedCustomTemplates(prev => [result.template, ...prev]);
            }
          } catch (_) {}
        }
      }

      setShowCustomerSelect(false);
      setShowCustomSend(false);
      setSelectedCustomers([]);
      setSendFromSaved(null);
      setCustomForm({ channel: 'email', subject: '', body: '', category: 'marketing' });
    } catch (err) {
      toast.error(err.message || 'Failed to send');
    } finally {
      setSendingCustom(false);
    }
  };

  const openCustomerSelect = () => {
    setSendFromSaved(null);
    setCustomerSearch('');
    setSelectedCustomers([]);
    loadCustomers();
    setShowCustomerSelect(true);
  };

  const toggleCustomerSelection = (customer) => {
    setSelectedCustomers(prev => {
      const exists = prev.find(c => c.id === customer.id);
      if (exists) return prev.filter(c => c.id !== customer.id);
      return [...prev, customer];
    });
  };

  const activeChannel = sendFromSaved ? sendFromSaved.channel : customForm.channel;

  const hasChannelContact = (c) => {
    if (activeChannel === 'email') return !!(c.email && c.email.trim()) && !c.emailUnsubscribed;
    if (activeChannel === 'sms') return !!(c.phone && c.phone.trim()) && !c.smsUnsubscribed;
    if (activeChannel === 'whatsapp') return !!(c.phone && c.phone.trim()) && !c.waUnsubscribed;
    return true;
  };

  const filteredCustomers = customers.filter(c => {
    if (!hasChannelContact(c)) return false;
    if (!customerSearch.trim()) return true;
    const q = customerSearch.toLowerCase();
    return c.name.toLowerCase().includes(q) || (c.email && c.email.toLowerCase().includes(q)) || (c.phone && c.phone.includes(q));
  });

  if (loading || !autoConfig) {
    const shimmer = { animation: 'pulse 1.5s ease-in-out infinite' };
    return (
      <div>
        <div className="page-header">
          <h1>Message Templates</h1>
          <p style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ height: 16, width: 280, background: 'var(--bg)', borderRadius: 4, display: 'inline-block', ...shimmer }} />
          </p>
        </div>

        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <span style={{ height: 14, width: 120, background: 'var(--bg)', borderRadius: 4, display: 'inline-block', ...shimmer }} />
            <span style={{ height: 18, width: 110, background: 'var(--bg)', borderRadius: 4, display: 'inline-block', ...shimmer }} />
            <span style={{ height: 18, width: 88, background: 'var(--bg)', borderRadius: 4, display: 'inline-block', ...shimmer }} />
            <span style={{ height: 18, width: 100, background: 'var(--bg)', borderRadius: 4, display: 'inline-block', ...shimmer }} />
          </div>
          <div style={{ height: 12, width: '95%', background: 'var(--bg)', borderRadius: 4, marginBottom: 4, ...shimmer }} />
          <div style={{ height: 12, width: '80%', background: 'var(--bg)', borderRadius: 4, ...shimmer }} />
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 12, padding: '16px 20px', marginBottom: 28,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ height: 18, width: 18, background: 'var(--bg)', borderRadius: 4, ...shimmer }} />
              <span style={{ height: 14, width: 220, background: 'var(--bg)', borderRadius: 4, ...shimmer }} />
            </div>
            <div style={{ margin: '6px 0 0 26px' }}>
              <div style={{ height: 12, width: '80%', background: 'var(--bg)', borderRadius: 4, ...shimmer }} />
            </div>
          </div>
          <div style={{ width: 48, height: 26, borderRadius: 13, background: 'var(--bg)', flexShrink: 0, ...shimmer }} />
        </div>

        {[1, 2].map(section => (
          <div key={section} style={{ marginBottom: 40 }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ height: 22, width: section === 1 ? 150 : 110, background: 'var(--bg)', borderRadius: 4, marginBottom: 6, ...shimmer }} />
              <div style={{ height: 13, width: section === 1 ? 310 : 290, background: 'var(--bg)', borderRadius: 4, marginBottom: 12, ...shimmer }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ height: 36, width: 340, background: 'var(--bg)', borderRadius: 8, ...shimmer }} />
                <div style={{ height: 36, width: 260, background: 'var(--bg)', borderRadius: 8, ...shimmer }} />
              </div>
            </div>
            <div className="template-grid">
              {[1, 2].map(card => (
                <div key={card} className="template-card">
                  <div className="template-header" style={{ marginBottom: 12 }}>
                    <div style={{ height: 18, width: 100, background: 'var(--bg)', borderRadius: 4, ...shimmer }} />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <div style={{ height: 30, width: 80, background: 'var(--bg)', borderRadius: 6, ...shimmer }} />
                      <div style={{ height: 30, width: 60, background: 'var(--bg)', borderRadius: 6, ...shimmer }} />
                      <div style={{ height: 30, width: 44, background: 'var(--bg)', borderRadius: 6, ...shimmer }} />
                    </div>
                  </div>
                  <div style={{ height: 13, width: '45%', background: 'var(--bg)', borderRadius: 4, marginBottom: 10, ...shimmer }} />
                  <div style={{ background: 'var(--bg)', borderRadius: 8, padding: 16 }}>
                    {[1, 2, 3, 4, 5].map(line => (
                      <div key={line} style={{ height: 13, width: `${95 - line * 10}%`, background: 'var(--surface)', borderRadius: 4, marginBottom: line < 5 ? 10 : 0, ...shimmer }} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  const sections = [
    {
      key: 'review_request',
      label: 'Review Requests',
      description: 'Sent after a customer visit to collect reviews',
    },
    {
      key: 'reminder',
      label: 'Reminders',
      description: 'Sent to bring customers back for another visit',
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>Message Templates</h1>
        <p>
          {business?.name
            ? <>Customize the messages sent by <strong>{business.name}</strong></>
            : 'Customize the messages sent to your customers'}
        </p>
      </div>


      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 24 }}>
        <p style={{ margin: '0 0 6px' }}>
          Available variables: <code>{'{{customer_name}}'}</code>{' '}
          <code>{'{{review_link}}'}</code> <code>{'{{booking_link}}'}</code>
        </p>
        <p style={{ margin: 0, fontSize: 12, opacity: 0.85 }}>
          Your business name is written directly into your default templates when your account is set up. It also appears automatically in email footers.{' '}
          <code>{'{{review_link}}'}</code> and <code>{'{{booking_link}}'}</code> are filled from your{' '}
          <strong>Settings</strong> when messages are sent.
        </p>
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 12, padding: '16px 20px', marginBottom: 28,
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>🔄</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
              Auto-refresh templates monthly
            </span>
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '4px 0 0 26px', lineHeight: 1.5 }}>
            Automatically updates your templates with fresh wording every 30 days so your messages stay engaging.
            {autoConfig?.lastTemplateRefreshAt && (
              <span style={{ marginLeft: 8, opacity: 0.8 }}>
                Last refreshed: {new Date(autoConfig.lastTemplateRefreshAt).toLocaleDateString()}
              </span>
            )}
          </p>
        </div>
        <button
          onClick={handleToggleAutoRefresh}
          disabled={togglingRefresh}
          role="switch"
          aria-checked={autoConfig?.autoRefreshTemplates}
          style={{
            position: 'relative',
            width: 48, height: 26, borderRadius: 13,
            border: 'none', cursor: togglingRefresh ? 'not-allowed' : 'pointer',
            background: autoConfig?.autoRefreshTemplates
              ? 'linear-gradient(135deg, #6C5CE7, #a855f7)'
              : '#D1D5DB',
            transition: 'background 0.25s ease',
            flexShrink: 0,
            opacity: togglingRefresh ? 0.6 : 1,
          }}
        >
          <span style={{
            position: 'absolute',
            top: 3, left: autoConfig?.autoRefreshTemplates ? 24 : 3,
            width: 20, height: 20, borderRadius: '50%',
            background: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            transition: 'left 0.25s ease',
          }} />
        </button>
      </div>

      {sections.map((section) => {
        const sectionTemplates = templates.filter((t) => t.category === section.key);
        if (sectionTemplates.length === 0) return null;

        return (
          <div key={section.key} style={{ marginBottom: 40 }}>
            <div style={{ marginBottom: 16 }}>
              <h2 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 4px' }}>{section.label}</h2>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 10px' }}>{section.description}</p>
              <TimingBadge
                config={autoConfig}
                editingConfig={editingConfig === section.key}
                configForm={configForm}
                setConfigForm={setConfigForm}
                onEdit={() => startConfigEdit(section.key)}
                onSave={saveConfig}
                onCancel={cancelConfigEdit}
                savingConfig={savingConfig}
                section={section.key}
              />
            </div>

            <div className="template-grid">
              {sectionTemplates.map((tpl) => {
                const isLocked = !channelAccess[tpl.channel];
                const lockMessage = tpl.channel === 'whatsapp'
                  ? 'WhatsApp is currently unavailable'
                  : tpl.channel === 'sms'
                    ? 'SMS is unavailable in your region'
                    : null;

                return (
                <div key={tpl.id} className="template-card" style={isLocked ? { opacity: 0.6, position: 'relative' } : undefined}>
                  {isLocked && (
                    <div style={{
                      position: 'absolute', top: 12, right: 12, background: '#FEF3C7',
                      color: '#92400E', fontSize: 11, padding: '4px 10px', borderRadius: 12,
                      fontWeight: 600, zIndex: 1,
                    }}>
                      Unavailable
                    </div>
                  )}
                  <div className="template-header">
                    <h3>{CHANNEL_LABELS[tpl.channel] || tpl.channel}</h3>
                    {!isLocked && (
                    <div style={{ display: 'flex', gap: 8 }}>
                      {tpl.channel === 'whatsapp' ? (
                        <button
                          className="btn btn-sm"
                          style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)', color: 'white' }}
                          onClick={() => handleCycleWaTemplate(tpl)}
                        >
                          🔄 Use Another Template
                        </button>
                      ) : (
                        <button
                          className="btn btn-sm"
                          style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', color: 'white' }}
                          onClick={() => handleImprove(tpl)}
                          disabled={improvingId !== null}
                        >
                          {improvingId === tpl.id ? (
                            <><span className="spinner" style={{ width: 14, height: 14 }} /> Thinking...</>
                          ) : '✨ Generate'}
                        </button>
                      )}
                      {editing === tpl.id ? (
                        <>
                          <button className="btn btn-secondary btn-sm" onClick={cancelEdit}>
                            Cancel
                          </button>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => setPreviewTpl({ ...tpl, subject: editForm.subject, body: editForm.body })}
                          >
                            Preview
                          </button>
                          <button className="btn btn-primary btn-sm" onClick={saveTemplate} disabled={saving || (tpl.channel === 'sms' && (editForm.body || '').length > SMS_MAX_LENGTH)}>
                            {saving ? <span className="spinner" /> : 'Save'}
                          </button>
                        </>
                      ) : (
                        <>
                          <button className="btn btn-secondary btn-sm" onClick={() => setPreviewTpl(tpl)}>
                            Preview
                          </button>
                          <button className="btn btn-secondary btn-sm" onClick={() => startEdit(tpl)}>
                            Edit
                          </button>
                        </>
                      )}
                    </div>
                    )}
                  </div>
                  {isLocked && lockMessage && (
                    <div style={{ fontSize: 13, color: '#92400E', background: '#FEF3C7', padding: '8px 12px', borderRadius: 8, marginBottom: 12 }}>
                      {lockMessage}
                    </div>
                  )}

                  {editing === tpl.id ? (
                    <>
                      {tpl.channel === 'email' && (
                        <div className="form-group">
                          <label>Subject</label>
                          <input
                            value={editForm.subject}
                            onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })}
                          />
                        </div>
                      )}
                      <div className="form-group">
                        <label>Message Body</label>
                        <textarea
                          value={editForm.body}
                          onChange={tpl.channel === 'whatsapp' ? undefined : (e) => setEditForm({ ...editForm, body: e.target.value })}
                          rows={6}
                          readOnly={tpl.channel === 'whatsapp'}
                          style={tpl.channel === 'whatsapp' ? { opacity: 0.7, cursor: 'not-allowed' } : undefined}
                        />
                        <SmsCharCounter text={editForm.body} channel={tpl.channel} />
                        {tpl.channel === 'whatsapp' && (
                          <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '6px 0 0', fontStyle: 'italic' }}>
                            WhatsApp templates are pre-approved by Meta and cannot be edited directly. Use &lsquo;Use Another Template&rsquo; to switch.
                          </p>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      {tpl.subject && (
                        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>
                          <strong>Subject:</strong> {tpl.subject}
                        </div>
                      )}
                      <pre
                        style={{
                          fontSize: 13,
                          whiteSpace: 'pre-wrap',
                          color: 'var(--text-secondary)',
                          background: 'var(--bg)',
                          padding: 16,
                          borderRadius: 8,
                          lineHeight: 1.6,
                        }}
                      >
                        {displayTemplateBody(tpl.body)}
                      </pre>
                    </>
                  )}
                </div>
              );
              })}
            </div>
          </div>
        );
      })}

      {/* Custom Templates Section */}
      <div style={{ marginTop: 40, borderTop: '2px solid var(--border)', paddingTop: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 4px' }}>Custom Templates</h2>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>Save custom message templates and send them to customers anytime</p>
          </div>
          {!showCustomSend && !editingSavedId && (
            <button className="btn btn-primary" onClick={() => setShowCustomSend(true)}>
              + Create Template
            </button>
          )}
        </div>


        {showCustomSend && (
          <div className="template-card" style={{ marginBottom: 24 }}>
            <div className="template-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <h3>{editingSavedId ? 'Edit Custom Template' : 'New Custom Template'}</h3>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select
                    value={customForm.channel}
                    onChange={(e) => setCustomForm({ ...customForm, channel: e.target.value })}
                    disabled={!!editingSavedId}
                    style={{
                      fontSize: 12, padding: '3px 8px', border: '1px solid var(--border)',
                      borderRadius: 6, background: 'var(--bg)', color: 'var(--text-secondary)',
                      cursor: editingSavedId ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {channelAccess.email && <option value="email">📧 Email</option>}
                    {channelAccess.sms && <option value="sms">💬 SMS</option>}
                    {channelAccess.whatsapp && <option value="whatsapp">📱 WhatsApp</option>}
                  </select>
                  <select
                    value={customForm.category}
                    onChange={(e) => setCustomForm({ ...customForm, category: e.target.value })}
                    disabled={!!editingSavedId}
                    style={{
                      fontSize: 12, padding: '3px 8px', border: '1px solid var(--border)',
                      borderRadius: 6, background: 'var(--bg)', color: 'var(--text-secondary)',
                      cursor: editingSavedId ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <option value="marketing">Marketing</option>
                    <option value="reminder">Reminder</option>
                    <option value="review_request">Review Request</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary btn-sm" onClick={closeCustomForm}>
                  Cancel
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => setPreviewTpl({ ...customForm, id: 'custom' })}>
                  Preview
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={handleSaveCustomTemplate}
                  disabled={savingCustomTemplate || (customForm.channel === 'sms' && customForm.body.length > SMS_MAX_LENGTH)}
                >
                  {savingCustomTemplate ? <><span className="spinner" style={{ width: 12, height: 12 }} /> Saving...</> : (editingSavedId ? 'Save Changes' : 'Save Template')}
                </button>
                {!editingSavedId && (
                  <button className="btn btn-primary btn-sm" onClick={openCustomerSelect}>
                    Select Customers & Send
                  </button>
                )}
              </div>
            </div>

            {customForm.channel === 'email' && (
              <div className="form-group">
                <label>Subject</label>
                <input
                  value={customForm.subject}
                  onChange={(e) => setCustomForm({ ...customForm, subject: e.target.value })}
                  placeholder="e.g. We miss you, {{customer_name}}!"
                />
              </div>
            )}

            <div className="form-group">
              <label>Message Body</label>
              <textarea
                value={customForm.body}
                onChange={(e) => setCustomForm({ ...customForm, body: e.target.value })}
                rows={6}
                placeholder="Write your message here. Use {{customer_name}} as a placeholder."
              />
              <SmsCharCounter text={customForm.body} channel={customForm.channel} />
            </div>
          </div>
        )}

        {/* Saved Custom Templates List */}
        {savedCustomTemplates.length > 0 && (
          <div className="template-grid">
            {savedCustomTemplates.map((tpl) => {
              if (editingSavedId === tpl.id) {
                return (
                  <div key={tpl.id} className="template-card">
                    <div className="template-header">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <h3>Edit Custom Template</h3>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <select
                            value={customForm.channel}
                            disabled
                            style={{
                              fontSize: 12, padding: '3px 8px', border: '1px solid var(--border)',
                              borderRadius: 6, background: 'var(--bg)', color: 'var(--text-secondary)',
                              cursor: 'not-allowed',
                            }}
                          >
                            {channelAccess.email && <option value="email">📧 Email</option>}
                            {channelAccess.sms && <option value="sms">💬 SMS</option>}
                            {channelAccess.whatsapp && <option value="whatsapp">📱 WhatsApp</option>}
                          </select>
                          <select
                            value={customForm.category}
                            disabled
                            style={{
                              fontSize: 12, padding: '3px 8px', border: '1px solid var(--border)',
                              borderRadius: 6, background: 'var(--bg)', color: 'var(--text-secondary)',
                              cursor: 'not-allowed',
                            }}
                          >
                            <option value="marketing">Marketing</option>
                            <option value="reminder">Reminder</option>
                            <option value="review_request">Review Request</option>
                          </select>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="btn btn-secondary btn-sm" onClick={closeCustomForm}>
                          Cancel
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={() => setPreviewTpl({ ...customForm, id: 'custom' })}>
                          Preview
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={handleSaveCustomTemplate}
                          disabled={savingCustomTemplate || (customForm.channel === 'sms' && customForm.body.length > SMS_MAX_LENGTH)}
                        >
                          {savingCustomTemplate ? <><span className="spinner" style={{ width: 12, height: 12 }} /> Saving...</> : 'Save Changes'}
                        </button>
                      </div>
                    </div>

                    {customForm.channel === 'email' && (
                      <div className="form-group">
                        <label>Subject</label>
                        <input
                          value={customForm.subject}
                          onChange={(e) => setCustomForm({ ...customForm, subject: e.target.value })}
                          placeholder="e.g. We miss you, {{customer_name}}!"
                        />
                      </div>
                    )}

                    <div className="form-group">
                      <label>Message Body</label>
                      <textarea
                        value={customForm.body}
                        onChange={(e) => setCustomForm({ ...customForm, body: e.target.value })}
                        rows={6}
                        placeholder="Write your message here. Use {{customer_name}} as a placeholder."
                      />
                      <SmsCharCounter text={customForm.body} channel={customForm.channel} />
                    </div>
                  </div>
                );
              }

              return (
                <div key={tpl.id} className="template-card">
                  <div className="template-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <h3>{CHANNEL_LABELS[tpl.channel] || tpl.channel}</h3>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: 12,
                        fontSize: 11,
                        background: '#f3f4f6',
                        color: '#4b5563',
                      }}>
                        {tpl.category === 'review_request' ? 'Review' : tpl.category === 'marketing' ? 'Marketing' : 'Reminder'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setPreviewTpl(tpl)}
                      >
                        Preview
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleEditSaved(tpl)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        style={{ color: '#EF4444' }}
                        onClick={() => setDeleteConfirm({ open: true, id: tpl.id })}
                      >
                        Delete
                      </button>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleUseSavedTemplate(tpl)}
                      >
                        Send
                      </button>
                    </div>
                  </div>
                  {tpl.subject && (
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>
                      <strong>Subject:</strong> {tpl.subject}
                    </div>
                  )}
                  <pre
                    style={{
                      fontSize: 13,
                      whiteSpace: 'pre-wrap',
                      color: 'var(--text-secondary)',
                      background: 'var(--bg)',
                      padding: 16,
                      borderRadius: 8,
                      lineHeight: 1.6,
                      margin: 0,
                    }}
                  >
                    {displayTemplateBody(tpl.body)}
                  </pre>
                </div>
              );
            })}
          </div>
        )}

        {savedCustomTemplates.length === 0 && !showCustomSend && (
          <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-secondary)' }}>
            <p style={{ fontSize: 14, margin: '0 0 8px' }}>No saved templates yet</p>
            <p style={{ fontSize: 12, margin: 0 }}>Create and save a template to reuse it anytime for quick sends to your customers</p>
          </div>
        )}
      </div>

      {/* Customer Selection Modal - Golden Ratio Layout */}
      {showCustomerSelect && (
        <div className="modal-overlay" onClick={() => { setShowCustomerSelect(false); setSendFromSaved(null); }}>
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: 680,
              width: '90vw',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              borderRadius: 16,
            }}
          >
            {/* Header - 1 part of golden ratio (compact) */}
            <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Select Customers</h2>
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-secondary)' }}>
                    {activeChannel === 'email'
                      ? 'Only showing subscribed customers with an email address'
                      : activeChannel === 'sms'
                      ? 'Only showing subscribed customers with a phone number'
                      : activeChannel === 'whatsapp'
                      ? 'Only showing subscribed customers with a phone number'
                      : null}
                  </p>
                </div>
                <button
                  onClick={() => { setShowCustomerSelect(false); setSendFromSaved(null); }}
                  style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text-secondary)', lineHeight: 1, padding: 4 }}
                >
                  &times;
                </button>
              </div>

              {/* Search Input */}
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={customerSearch}
                  onChange={(e) => { setCustomerSearch(e.target.value); setActiveSegment(null); }}
                  placeholder="Search by name, email, or phone..."
                  style={{
                    width: '100%',
                    padding: '10px 14px 10px 38px',
                    border: '1.5px solid var(--border)',
                    borderRadius: 10,
                    fontSize: 14,
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    background: 'var(--bg-secondary, #f9fafb)',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = '#6c5ce7'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'var(--border)'; }}
                  autoFocus
                />
                <svg
                  style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', opacity: 0.4 }}
                  width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                </svg>
              </div>

              {/* Smart Segments */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                {[
                  { id: 'last30', label: 'Added in last 30 days', filter: c => c.lastVisitAt && (Date.now() - new Date(c.lastVisitAt).getTime()) <= 30 * 86400000 },
                  { id: 'ago30', label: 'Added 30+ days ago', filter: c => c.lastVisitAt && (Date.now() - new Date(c.lastVisitAt).getTime()) > 30 * 86400000 },
                  { id: 'ago60', label: 'Added 60+ days ago', filter: c => c.lastVisitAt && (Date.now() - new Date(c.lastVisitAt).getTime()) > 60 * 86400000 },
                  { id: 'inactive', label: 'Inactive', filter: c => !c.lastVisitAt || (Date.now() - new Date(c.lastVisitAt).getTime()) > 90 * 86400000 },
                ].map(seg => (
                  <button
                    key={seg.id}
                    onClick={() => {
                      if (activeSegment === seg.id) {
                        setActiveSegment(null);
                        setSelectedCustomers([]);
                      } else {
                        setActiveSegment(seg.id);
                        setCustomerSearch('');
                        const matched = customers.filter(c => hasChannelContact(c) && seg.filter(c));
                        setSelectedCustomers(matched);
                      }
                    }}
                    style={{
                      padding: '4px 10px', borderRadius: 16, border: '1px solid var(--border)',
                      fontSize: 11, fontWeight: 500, cursor: 'pointer',
                      background: activeSegment === seg.id ? '#6c5ce7' : 'var(--bg)',
                      color: activeSegment === seg.id ? 'white' : 'var(--text-secondary)',
                      transition: 'all 0.15s',
                    }}
                  >
                    {seg.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Customer List - 1.618 parts (golden ratio larger section) */}
            <div style={{ flex: '1.618', overflowY: 'auto', padding: '0' }}>
              {loadingCustomers ? (
                <div style={{ textAlign: 'center', padding: 48 }}><span className="spinner" /></div>
              ) : filteredCustomers.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
                  {customers.length === 0 ? (
                    <p>No customers found. Add customers first.</p>
                  ) : customers.filter(hasChannelContact).length === 0 ? (
                    <p>
                      No customers have a{activeChannel === 'email' ? 'n email address' : ' phone number'} on record.
                      Add {activeChannel === 'email' ? 'email addresses' : 'phone numbers'} to your customers to send via {activeChannel}.
                    </p>
                  ) : (
                    <p>No customers match "{customerSearch}"</p>
                  )}
                </div>
              ) : (
                <div>
                  {/* Select all / deselect all */}
                  <div style={{
                    padding: '8px 24px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid var(--border)',
                    background: 'var(--bg-secondary, #f9fafb)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                  }}>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {filteredCustomers.length} customer{filteredCustomers.length !== 1 ? 's' : ''} found
                    </span>
                    <button
                      style={{ fontSize: 12, background: 'none', border: 'none', color: '#6c5ce7', cursor: 'pointer', fontWeight: 500 }}
                      onClick={() => {
                        const allSelected = filteredCustomers.every(c => selectedCustomers.find(s => s.id === c.id));
                        if (allSelected) {
                          setSelectedCustomers(prev => prev.filter(s => !filteredCustomers.find(c => c.id === s.id)));
                        } else {
                          setSelectedCustomers(prev => {
                            const newOnes = filteredCustomers.filter(c => !prev.find(s => s.id === c.id));
                            return [...prev, ...newOnes];
                          });
                        }
                      }}
                    >
                      {filteredCustomers.every(c => selectedCustomers.find(s => s.id === c.id)) ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>

                  {filteredCustomers.map((cust) => {
                    const isSelected = selectedCustomers.find(c => c.id === cust.id);
                    return (
                      <div
                        key={cust.id}
                        onClick={() => toggleCustomerSelection(cust)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 14,
                          padding: '12px 24px',
                          borderBottom: '1px solid var(--border)',
                          cursor: 'pointer',
                          background: isSelected ? 'rgba(108, 92, 231, 0.04)' : 'transparent',
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = 'rgba(108, 92, 231, 0.02)'; }}
                        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                      >
                        <div style={{
                          width: 20, height: 20, borderRadius: 4,
                          border: isSelected ? '2px solid #6c5ce7' : '2px solid #d1d5db',
                          background: isSelected ? '#6c5ce7' : 'transparent',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'all 0.15s', flexShrink: 0,
                        }}>
                          {isSelected && (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </div>
                        <div style={{
                          width: 36, height: 36, borderRadius: '50%',
                          background: `hsl(${(cust.name || '').charCodeAt(0) * 7 % 360}, 60%, 90%)`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 14, fontWeight: 600,
                          color: `hsl(${(cust.name || '').charCodeAt(0) * 7 % 360}, 60%, 35%)`,
                          flexShrink: 0,
                        }}>
                          {(cust.name || '?').charAt(0).toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cust.name}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cust.email}{cust.phone ? ` • ${cust.phone}` : ''}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer - 1 part of golden ratio (compact action bar) */}
            <div style={{
              padding: '14px 24px',
              borderTop: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0,
              background: 'var(--bg-secondary, #f9fafb)',
            }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: selectedCustomers.length > 0 ? '#6c5ce7' : 'var(--text-secondary)' }}>
                {selectedCustomers.length} selected
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary" onClick={() => { setShowCustomerSelect(false); setSendFromSaved(null); }}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleCustomSend}
                  disabled={sendingCustom || selectedCustomers.length === 0}
                  style={{ minWidth: 140 }}
                >
                  {sendingCustom ? (
                    <><span className="spinner" style={{ width: 14, height: 14 }} /> Sending...</>
                  ) : (
                    `Send to ${selectedCustomers.length} Customer${selectedCustomers.length !== 1 ? 's' : ''}`
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <PreviewModal tpl={previewTpl} business={business} onClose={() => setPreviewTpl(null)} />

      {/* Delete Confirmation Dialog */}
      {deleteConfirm.open && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm({ open: false, id: null })}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400, padding: 28 }}>
            <h2 style={{ margin: '0 0 12px', fontSize: 18 }}>Delete Template?</h2>
            <p style={{ margin: '0 0 24px', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              Are you sure you want to delete this template? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className="btn btn-secondary" onClick={() => setDeleteConfirm({ open: false, id: null })}>Cancel</button>
              <button className="btn" style={{ background: '#EF4444', color: 'white' }} onClick={() => handleDeleteSavedTemplate(deleteConfirm.id)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
